import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

import { DefaultImageDirective } from '@shared/directives';
import { isMobileView } from '@core/utils';
import { IconComponent } from '@shared/components/ui';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-lightbox-gallery',
  standalone: true,
  imports: [IconComponent, DefaultImageDirective],
  templateUrl: './lightbox-gallery.component.html',
  styleUrls: ['./lightbox-gallery.component.scss'],
})
export class LightboxGalleryComponent {
  @Input() images: string[] = [];
  @Input() currentImageIndex = 0;
  @Output() hide: EventEmitter<boolean> = new EventEmitter<boolean>();

  get currentImageUrl(): string {
    return this.images[this.currentImageIndex];
  }

  @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft' && !this.isPreviousDisabled()) {
      this.showPreviousImage();
    } else if (event.key === 'ArrowRight' && !this.isNextDisabled()) {
      this.showNextImage();
    } else if (event.key === 'Escape') {
      this.onClose();
    }
  }

  showPreviousImage(): void {
    this.currentImageIndex = (this.currentImageIndex - 1 + this.images.length) % this.images.length;
  }

  showNextImage(): void {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
  }

  isPreviousDisabled(): boolean {
    return this.images.length === 0 || this.currentImageIndex === 0;
  }

  isNextDisabled(): boolean {
    return this.images.length === 0 || this.currentImageIndex === this.images.length - 1;
  }

  onClose() {
    this.hide.emit(false);
  }

  // Helper function to convert URL to Data URL
  async toDataURL(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectURL = URL.createObjectURL(blob);
    return objectURL;
  }

  async downloadImage(url: string) {
    try {
      const filename = this._getFilenameFromFirebaseUrl(url);

      // Try fetch first (works if CORS allows or with proper CORS headers)
      fetch(url, { mode: 'cors' })
        .then((response) => {
          if (!response.ok) throw new Error(`Fetch failed; HTTP status: ${response.status}`);
          return response.blob();
        })
        .then((blob) => {
          // Successfully fetched - use file-saver for download
          saveAs(blob, filename);
          console.log('Image downloaded:', filename);
        })
        .catch(() => {
          // CORS or network error
          if (isMobileView()) {
            // Mobile PWA: Use anchor element for native download
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log('Image downloading natively (mobile):', filename);
          } else {
            // Web/Desktop: Open in new browser tab
            window.open(url, '_blank');
            console.log('Opening image in new browser tab:', filename);
          }
        });
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }

  openUrlInNewTab() {
    if (!isMobileView()) window.open(this.currentImageUrl, '_blank');
  }

  private _getFilenameFromFirebaseUrl(url: string): string {
    try {
      const afterO = url.split('o/')[1];
      const beforeQuery = afterO.split('?')[0];
      const decodedPath = decodeURIComponent(beforeQuery);
      const filename = decodedPath.split('/').pop() || 'download.jpg';
      return filename;
    } catch (err) {
      console.warn('Error extracting filename from URL:', err);
      // Fallback name
      return 'download.jpg';
    }
  }
}
